<template>
    <div v-scroll id="app">
        <div class="header">
            <div class="header-bgd" :style="bgdStyle">
                <div />
                <div />
                <div />
            </div>
            <div class="navbar-wrapper">
                <Navbar v-bind:white="true" @menu-click="onMenuClick" />
            </div>
            <div class="title-wrapper row-wrap">
                <div class="row align-center" id="avatar-wrapper">
                    <img src="@/assets/icons/other/example-profile.png" id="avatar" />
                    <h2 class="white-text name">{{ name }}</h2>
                </div>
                <div class="action-wrapper" @click="toggleResetModal">
                    <LockIcon :style="{ stroke: 'white' }" />
                    <p class="white-text">Reset password</p>
                </div>
            </div>
        </div>

        <div class="body">
            <div class="announcement row-wrap">
                <ReviewsIcon :style="reviewsIconStyle" />
                <p class="announcement-text">
                    <span class="soft-text" v-html="announcementText"></span> <br />
                    <span class="minor-text">{{ announcementSubtext }}</span>
                </p>
                <div class="row add-button" @click="toggleRatingModal">
                    <AddIcon />
                    <p class="soft-text">submit a new review</p>
                </div>
            </div>
            <div class="content">
                <div class="section user-info">
                    <h5 class="soft-text">Your school</h5>
                    <UWLogo :style="{ padding: '28px 0 16px 0', 'min-width': '200px', 'max-width': '348px' }" />
                    <div class="user-field" id="major">
                        <h5 class="label minor-text">Major</h5>
                        <span class="input-field">
                            <p
                                class="input-text"
                                v-for="(currMajor, currIdx) in profileMajor"
                                :key="`curr-major-${currIdx}`"
                            >
                                {{ currMajor }}
                                <CloseIcon
                                    v-if="editingMajor"
                                    :style="{ width: 12, height: 10, stroke: '#999999' }"
                                    @click="removeMajor(currIdx)"
                                />
                            </p>
                            <p
                                class="action-text minor-text"
                                v-if="!openMajorDropdown && !editingMajor"
                                @click="toggleMajorEdit"
                            >
                                Edit
                            </p>

                            <p class="action-text minor-text" v-if="editingMajor" @click="toggleMajorDropdown">
                                Add a major
                            </p>

                            <p class="action-text success-text" v-if="editingMajor" @click="submitMajorEdit">Done</p>
                        </span>
                        <div class="web dropdown" id="majorDropdown" v-if="openMajorDropdown">
                            <p
                                class="dropdown-option"
                                v-for="(major, idx) in activeMajors"
                                :key="`major-${idx}`"
                                @click="selectMajor(major)"
                            >
                                {{ major }}
                            </p>
                        </div>
                    </div>
                    <div class="user-field">
                        <h5 class="label minor-text">Class Standing</h5>
                        <span class="input-field">
                            <p class="input-text">{{ profileClass }}</p>
                            <ExpandIcon :style="{ stroke: '#504141' }" @click="toggleClassDropdown" />
                        </span>
                        <div class="web dropdown" id="classDropdown" v-if="openClassDropdown">
                            <span class="dropdown-title">
                                <h5>{{ profileClass }}</h5>
                                <ShrinkIcon :style="{ stroke: '#504141' }" @click="toggleClassDropdown" />
                            </span>
                            <p
                                class="dropdown-option"
                                v-for="(label, idx) in activeClassStandings"
                                :key="`class-${idx}`"
                                @click="selectClassStanding(label)"
                            >
                                {{ label }}
                            </p>
                        </div>
                    </div>
                    <div class="divider" />
                    <h5 class="soft-text">Your account</h5>
                    <div class="user-field" id="email">
                        <h5 class="label minor-text">Email</h5>
                        <p class="input-text"> {{ email }} </p>
                    </div>
                    <div class="action-wrapper" @click="toggleResetModal">
                        <LockIcon :style="{ stroke: '#999999' }" />
                        <p class="minor-text">Reset password</p>
                    </div>
                    <div class="action-wrapper" @click="logout">
                        <LogoutIcon :style="{ stroke: '#999999' }" />
                        <p class="minor-text">Log out</p>
                    </div>
                </div>
                <div class="section placeholder col">
                    <h4 class="minor-text">New features coming soon</h4>
                    <a href="https://forms.gle/egsbkbSQS3QPXhDD9" target="_blank">
                        <button class="suggest-button"><p class="primary-text">Suggest a feature</p></button>
                    </a>
                </div>
            </div>
        </div>

        <Modal
            :isOpen="majorModalOpen"
            v-on:toggleModal="toggleMajorModal"
            v-if="majorModalOpen"
            minWidth="100%"
            minHeight="100%"
        >
            <template v-slot:body>
                <p
                    class="dropdown-option mobile"
                    v-for="(major, idx) in activeMajors"
                    :key="`major-mobile-${idx}`"
                    @click="selectMajor(major)"
                >
                    {{ major }}
                </p>
            </template>
        </Modal>

        <Modal
            :isOpen="classModalOpen"
            v-on:toggleModal="toggleClassModal"
            v-if="classModalOpen"
            minWidth="100%"
            minHeight="100%"
        >
            <template v-slot:body>
                <p
                    class="dropdown-option mobile"
                    v-for="(classLabel, idx) in activeClassStandings"
                    :key="`class-mobile-${idx}`"
                    @click="selectClassStanding(classLabel)"
                >
                    {{ classLabel }}
                </p>
            </template>
        </Modal>

        <RatingSelectionModal :isOpen="ratingModalOpen" v-on:toggleModal="toggleRatingModal" />
        <LoginModal ref="resetModal" display="reset" :isOpen="resetModalOpen" @toggleModal="toggleResetModal" />

        <Footer />
    </div>
</template>

<script>
// components
import Navbar from "@/components/navigation/Navbar";
import Footer from "@/components/navigation/Footer";
import Modal from "@/components/modals/Modal";
import RatingSelectionModal from "@/components/modals/RatingSelectionModal";
import LoginModal from "@/components/modals/LoginModal";

// icons
import LockIcon from "@/assets/icons/other/inline.lock-icon.svg";
import ReviewsIcon from "@/assets/visuals/inline.reviews-submitted.svg";
import AddIcon from "@/assets/icons/actions/inline.add-icon.svg";
import ExpandIcon from "@/assets/icons/actions/inline.expand-icon.svg";
import ShrinkIcon from "@/assets/icons/actions/inline.shrink-icon.svg";
import CloseIcon from "@/assets/icons/actions/inline.close-icon.svg";
import LogoutIcon from "@/assets/icons/actions/inline.logout-icon.svg";
import UWLogo from "@/assets/visuals/inline.uw.svg";

// libraries
import { firestore, auth } from "@/firebase.js";

// mixins
import utilMixin from "@/mixins/utilMixin.js";

// data
import profileJSON from "@/data/profile.json";

export default {
    name: "ProfilePage",

    components: {
        Navbar,
        Footer,
        Modal,
        RatingSelectionModal,
        LoginModal,
        LockIcon,
        ReviewsIcon,
        AddIcon,
        ExpandIcon,
        ShrinkIcon,
        CloseIcon,
        LogoutIcon,
        UWLogo,
    },

    mixins: [utilMixin],

    data() {
        return {

            // profile info
            name: "",
            email: "",
            profileMajor: ["Computer Science"],
            profileClass: "Freshman",
            numReviewsSubmitted: 0,

            // action / state variables
            openMenu: false,
            editingMajor: false,
            openMajorDropdown: false,
            openClassDropdown: false,
            resetModalOpen: false,
            ratingModalOpen: false,
            majorModalOpen: false,
            classModalOpen: false,

            // styles
            reviewsIconStyle: this.iconPaddingStyle(),
        };
    },

    created() {
        window.addEventListener("resize", () => (this.reviewsIconStyle = this.iconPaddingStyle()));
    },

    destroyed() {
        window.removeEventListener("resize", () => (this.reviewsIconStyle = this.iconPaddingStyle()));
    },

    computed: {
        // style
        bgdStyle() {
            if (this.openMenu) {
                let height = window.innerWidth < 480 ? "275px" : "362px";
                return {
                    height: height,
                };
            } else {
                return {};
            }
        },
        // labels
        announcementText() {
            if (!this.numReviewsSubmitted || this.numReviewsSubmitted == 0) {
                return `You have not submitted a review yet`;
            } else {
                return `<span class="enlarged-text">${this.numReviewsSubmitted}</span> course reviews submitted`;
            }
        },
        announcementSubtext() {
            if (!this.numReviewsSubmitted || this.numReviewsSubmitted == 0) {
                return `Everyone is invited, you don't want to miss out the fun 😏`;
            } else {
                return `It's not party time yet, gonna take some hours into the community building 💕`;
            }
        },
        activeMajors() {
            return profileJSON.majors.filter((m) => !this.profileMajor.includes(m));
        },
        activeClassStandings() {
            return profileJSON.classStandings.filter((c) => c != this.profileClass);
        },
    },

    methods: {
        // display events
        onMenuClick() {
            this.openMenu = !this.openMenu;
        },
        toggleResetModal() {
            this.resetModalOpen = !this.resetModalOpen;
            this.$refs.resetModal.$forceUpdate();
        },
        toggleRatingModal() {
            this.ratingModalOpen = !this.ratingModalOpen;
        },
        toggleMajorModal() {
            this.majorModalOpen = !this.majorModalOpen;
        },
        toggleClassModal() {
            this.classModalOpen = !this.classModalOpen;
        },
        logout() {
            auth.signOut()
            .then(() => {
                this.$router.push({ name: "home" })
            });
        },

        // class selection events
        openDropdown(e, dropdownId, toggleModal, toggleDropdown) {
            e.stopPropagation();
            if (this.isMobile()) {
                toggleModal();
            } else {
                const clickOutside = (event) => {
                    event.stopPropagation();
                    let elem = document.getElementById(dropdownId);
                    if (elem != event.target && !elem.contains(event.target)) {
                        document.removeEventListener("click", clickOutside);
                        toggleDropdown();
                    }
                };
                document.addEventListener("click", clickOutside);
            }
        },
        toggleClassDropdown(e) {
            if (this.openMajorDropdown) return;
            this.openClassDropdown = !this.openClassDropdown;
            if (this.openClassDropdown)
                this.openDropdown(e, "classDropdown", this.toggleClassModal, this.toggleClassDropdown);
        },
        selectClassStanding(option) {
            this.profileClass = option;

            firestore
                .collection("university").doc("uw")
                .collection("users").doc(auth.currentUser.uid)
                .update({
                    class: option
                })
                .then(() => {
                    if (this.isMobile()) this.toggleClassModal();
                    this.toggleClassDropdown();
                })
                .catch((error) => {
                    console.log(error);
                });
        },

        // major selection events
        toggleMajorEdit() {
            this.editingMajor = !this.editingMajor;
        },
        toggleMajorDropdown(e) {
            if (this.openClassDropdown) return;
            this.openMajorDropdown = !this.openMajorDropdown;
            if (this.openMajorDropdown)
                this.openDropdown(e, "majorDropdown", this.toggleMajorModal, this.toggleMajorDropdown);
        },
        selectMajor(option) {
            this.profileMajor.push(option);
            if (this.isMobile()) this.toggleMajorModal();
            this.toggleMajorDropdown();
        },
        removeMajor(idx) {
            this.profileMajor.splice(idx, 1);
        },
        submitMajorEdit() {

            // Send an update
            firestore
                .collection("university").doc("uw")
                .collection("users").doc(auth.currentUser.uid)
                .update({
                    major: this.profileMajor
                })
                .then(() => {
                    this.toggleMajorEdit();
                })
                .catch((error) => {
                    console.log(error);
                });

        },

        // style
        iconPaddingStyle() {
            let width = window.innerWidth;
            if (width < 512) {
                return { display: "none" };
            } else if (width < 768) {
                return { "padding-right": "10px" };
            } else {
                return { "padding-right": "25px" };
            }
        },


    },

    mounted() {


        // Check to see whether we are logged in
        auth.onAuthStateChanged((user) => {
            if (user) {
                firestore
                    .collection("university").doc("uw")
                    .collection("users").doc(auth.currentUser.uid)
                    .get()
                    .then((snap) => {
                        if (snap.exists) {
                            let data = snap.data();

                            this.name = data.name;
                            this.email = auth.currentUser.email;
                            this.profileMajor = data.major;
                            this.profileClass = data.class;
							this.numReviewsSubmitted = (data.numReviews) ? data.numReviews : 0;
                        }
                    })
                    .catch(() => {
                        this.$router.push({ name: "home" , props: { showLoginModal: true }});
                    });
            } else {
                this.$router.push({ name: "login" });
            }
        });
    }
};
</script>

<style lang="scss" scoped>
@import "@/styles/_main.scss";

// General styles
p {
    font-size: 14px;
}

/deep/ .enlarged-text {
    font-size: 20px;
    padding-right: 6px;
}

.action-wrapper {
    @extend .row;
    @extend .align-baseline;
    min-width: max-content;
    cursor: pointer;

    p {
        margin: 0;
        line-height: 14px;
        font-size: 14px !important;
    }

    svg {
        padding-right: 10px;
    }
}

// Header styles
.header-bgd {
    position: absolute;
    z-index: -1;
    overflow: hidden;

    width: 100%;
    height: 262px;
    background-color: #666666;
    border-radius: 0 0 5rem 5rem;

    > * {
        margin-top: 35px;
        width: 100%;
        height: 35px;
        background: url("../assets/visuals/profile-bgd.svg");
    }

    :nth-child(1) {
        background-position-x: 60px;
    }

    :nth-child(2) {
        background-position-x: -80px;
    }

    :nth-child(3) {
        background-position-x: -340px;
    }
}

.header {
    overflow: hidden;
}

.header.row {
    min-width: fit-content;
}

.title-wrapper,
.body {
    padding: 25px 8%;
}

.title-wrapper {
    justify-content: space-between;
}

#avatar-wrapper {
    flex: 1;
}

#avatar {
    padding-right: 36px;
    max-height: 150px;
    max-width: 150px;
    height: 100%;
    width: auto;
}

.name {
    min-width: 200px;
}

// Announcement styles
.announcement {
    padding: 17px 28px;
    align-items: center;
    width: auto;

    background: #f4e5de;
    border: 1px solid #e3b4a5;
    border-radius: 12px;
}

.announcement-text {
    flex: 1;
    min-width: 200px;
}

.add-button {
    @extend .align-center;
    justify-content: flex-end;
    min-width: 200px;
    cursor: pointer;

    p {
        margin: 0;
    }

    svg {
        filter: invert(38%) sepia(1%) saturate(0%) hue-rotate(184deg) brightness(103%) contrast(90%);
        padding-right: 8px;
    }
}

// Main content styles
.content {
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 32px;
    margin-top: 24px;
    margin-bottom: 24px;
}

.content .section {
    padding: 36px 48px;
    background: #fcf0ef;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    border-radius: 12px;
}

.divider {
    margin: 41.5px 0;
    width: 100%;
    height: 1px;
    opacity: 0.3;
    background: #999999;
}

.content .action-wrapper {
    margin-top: 16px;
}

// User info styles
.user-info h5 {
    font-size: 16px;
    font-weight: normal;
}

.user-field {
    @extend .align-center;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    column-gap: 10px;
}

.user-field#major {
    @extend .align-start;

    .input-field {
        @extend .col;
        @extend .align-start;
        margin-top: -16px;

        p {
            margin: 8px 0 0 0;
            line-height: 30px;
        }

        p:last-child() {
            padding-bottom: 12px;
        }
    }
}

.user-field#email {
    margin-top: 21.5px;
    margin-bottom: 8px;
}

.input-field {
    @extend .row;
    @extend .align-center;
    min-width: max-content;

    svg {
        padding-left: 12px;
        cursor: pointer;
    }
}

.input-text {
    font-size: 16px;
    margin: 8px 0;
}

.action-text {
    font-size: 16px;
    cursor: pointer;
}

.dropdown {
    padding: 16px 20px;
    margin: -40px 0 0 15%;
    position: absolute;
    z-index: 1;

    min-width: 200px;
    max-width: 325px;
    max-height: 400px;
    overflow-y: scroll;

    background: #fcf0ef;
    box-shadow: 0px 6px 24px rgba(80, 65, 65, 0.2);
    border-radius: 12px;

    p {
        @extend .soft-text;
        font-size: 14px;
        margin: 8px 0;
    }

    p:first-of-type() {
        margin-top: 16px;
    }

    p:last-of-type() {
        margin-bottom: 0;
    }
}

.dropdown-title {
    @extend .row;
    @extend .align-center;

    h5 {
        padding-right: 12px;
        color: #504141;
    }

    svg {
        cursor: pointer;
    }
}

.dropdown-option {
    cursor: pointer;
}

.dropdown-option.mobile {
    width: 100%;
}

// Placeholder styles
.placeholder {
    @extend .align-center;
    justify-content: center;
    min-width: fit-content;
}

.placeholder h4 {
    width: 100%;
    text-align: center;
    font-size: 20px;
}

.suggest-button {
    margin-top: 30px;
    padding: 12px 24px;

    background: transparent;
    border: 1px solid $primary;
    outline: none;
    box-sizing: border-box;
    border-radius: 12px;
    cursor: pointer;

    p {
        padding: 0;
        margin: 0;
    }
}

// media queries
@media only screen and (max-width: 900px) {
    // main content styles
    .content {
        grid-template-columns: repeat(auto-fit, minmax(300px, auto));
        row-gap: 24px;
    }
}

@media only screen and (max-width: $breakpoint-lg-medium) {
    // header styles
    #action-wrapper {
        display: none;
    }
}

@media only screen and (max-width: 680px) {
    // announcement styles
    .add-button {
        justify-content: flex-start;
    }
}

@media only screen and (max-width: $breakpoint-medium) {
    // header styles
    .header-bgd {
        height: 232px;
    }

    .title-wrapper {
        padding-bottom: 0;
    }

    .body {
        padding-top: 0;
    }

    #avatar-wrapper {
        justify-content: center;
    }

    #avatar {
        padding-right: 20px;
        max-height: 120px;
        max-width: 120px;
    }

    .name {
        font-size: 24px;
        width: fit-content;
    }
}

@media only screen and (max-width: $breakpoint-small) {
    // header styles
    .header-bgd {
        height: 172px;
        border-radius: 0 0 2rem 2rem;
    }

    .title-wrapper {
        padding: 0;
        margin: 0;
    }

    .body {
        padding: 0 25px;
        margin: 0;
    }

    #avatar {
        max-height: 85px;
        max-width: 85px;
    }

    .name {
        font-size: 20px;
    }

    // announcement styles
    .announcement {
        padding: 20px 24px;
    }

    .announcement-text {
        margin: 0;

        :first-child() {
            line-height: 32px;
        }
    }

    .add-button {
        margin-top: 20px;
    }

    /deep/ .enlarged-text {
        font-size: 24px;
    }

    // main content styles
    .content .section {
        padding: 36px 24px;
    }

    // user info styles
    .user-info {
        p,
        h5 {
            font-size: 14px;
        }
    }

    .user-field#email {
        margin-bottom: 0;

        .input-text {
            margin: 0;
        }
    }

    // placeholder styles
    .placeholder h4 {
        font-size: 16px;
    }

    .suggest-button {
        margin-top: 20px;
    }
}
</style>
